import React, { useState, useEffect } from 'react';
import { useCompany } from '../context/CompanyContext';
import '../components/Common/Common.css';

function CompanySettings() {
  const { companySettings, loading: contextLoading, updateCompanySettings, uploadLogo, deleteLogo, getLogoUrl } = useCompany();

  const [settings, setSettings] = useState({
    company_name: '',
    company_email: '',
    company_phone: '',
    company_address: '',
    company_city: '',
    company_country: '',
    company_website: '',
    tax_id: '',
    currency: 'USD',
    currency_symbol: '$',
  });
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [logoPreview, setLogoPreview] = useState(null);

  useEffect(() => {
    if (companySettings) {
      setSettings({
        company_name: companySettings.company_name || '',
        company_email: companySettings.company_email || '',
        company_phone: companySettings.company_phone || '',
        company_address: companySettings.company_address || '',
        company_city: companySettings.company_city || '',
        company_country: companySettings.company_country || '',
        company_website: companySettings.company_website || '',
        tax_id: companySettings.tax_id || '',
        currency: companySettings.currency || 'USD',
        currency_symbol: companySettings.currency_symbol || '$',
      });
    }
  }, [companySettings]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Veuillez sélectionner un fichier image');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError('La taille de l\'image doit être inférieure à 2 Mo');
      return;
    }

    setUploadingLogo(true);
    setError('');
    setMessage('');

    try {
      await uploadLogo(file);
      setMessage('Logo téléchargé avec succès !');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Échec du téléchargement du logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer le logo ?')) {
      return;
    }

    setUploadingLogo(true);
    setError('');
    setMessage('');

    try {
      await deleteLogo();
      setMessage('Logo supprimé avec succès !');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Échec de la suppression du logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    setError('');

    try {
      await updateCompanySettings(settings);
      setMessage('Paramètres de l\'entreprise enregistrés avec succès !');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Échec de l\'enregistrement des paramètres');
    } finally {
      setSaving(false);
    }
  };

  if (contextLoading) {
    return <div className="loading">Chargement des paramètres...</div>;
  }

  const logoUrl = getLogoUrl();

  return (
    <div>
      <div className="page-header">
        <h1>Paramètres de l'entreprise</h1>
      </div>

      {message && <div className="success-message" style={{marginBottom: '20px', padding: '12px', background: '#d4edda', color: '#155724', borderRadius: '6px'}}>{message}</div>}
      {error && <div className="error-message" style={{marginBottom: '20px', padding: '12px', background: '#f8d7da', color: '#721c24', borderRadius: '6px'}}>{error}</div>}

      {/* Logo Section */}
      <div className="card" style={{marginBottom: '20px'}}>
        <div className="card-header">
          <h2>Logo de l'entreprise</h2>
        </div>
        <div className="card-body">
          {logoUrl && (
            <div style={{marginBottom: '20px', textAlign: 'center'}}>
              <img
                src={logoUrl}
                alt="Logo de l'entreprise"
                style={{maxWidth: '300px', maxHeight: '150px', border: '1px solid #ddd', borderRadius: '8px', padding: '10px'}}
              />
            </div>
          )}
          <div className="form-group">
            <label>Télécharger un nouveau logo</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleLogoChange}
              disabled={uploadingLogo}
              style={{marginBottom: '10px'}}
            />
            <p style={{fontSize: '12px', color: '#666', margin: '8px 0'}}>
              Taille maximale du fichier : 2 Mo. Formats supportés : JPG, PNG, GIF, SVG
            </p>
            {logoUrl && (
              <button
                type="button"
                onClick={handleDeleteLogo}
                className="btn btn-danger"
                disabled={uploadingLogo}
                style={{marginTop: '10px', background: '#dc3545', color: 'white', border: 'none'}}
              >
                {uploadingLogo ? 'Suppression...' : 'Supprimer le logo'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Company Information Section */}
      <div className="card">
        <form onSubmit={handleSubmit}>
          <div className="card-header">
            <h2>Informations de l'entreprise</h2>
          </div>
          <div className="card-body">
            <div className="form-row">
              <div className="form-group">
                <label>Nom de l'entreprise *</label>
                <input
                  type="text"
                  name="company_name"
                  value={settings.company_name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="company_email"
                  value={settings.company_email}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Téléphone</label>
                <input
                  type="text"
                  name="company_phone"
                  value={settings.company_phone}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label>Site web</label>
                <input
                  type="text"
                  name="company_website"
                  value={settings.company_website}
                  onChange={handleChange}
                  placeholder="https://..."
                />
              </div>
            </div>

            <div className="form-group">
              <label>Adresse</label>
              <input
                type="text"
                name="company_address"
                value={settings.company_address}
                onChange={handleChange}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Ville</label>
                <input
                  type="text"
                  name="company_city"
                  value={settings.company_city}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label>Pays</label>
                <input
                  type="text"
                  name="company_country"
                  value={settings.company_country}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Numéro de TVA / Identifiant fiscal</label>
              <input
                type="text"
                name="tax_id"
                value={settings.tax_id}
                onChange={handleChange}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Code de devise</label>
                <input
                  type="text"
                  name="currency"
                  value={settings.currency}
                  onChange={handleChange}
                  maxLength="10"
                  placeholder="MAD"
                />
                <p style={{fontSize: '12px', color: '#666', margin: '4px 0 0 0'}}>
                  Par ex. : MAD, EUR, USD, GBP
                </p>
              </div>

              <div className="form-group">
                <label>Symbole de devise *</label>
                <input
                  type="text"
                  name="currency_symbol"
                  value={settings.currency_symbol}
                  onChange={handleChange}
                  maxLength="10"
                  placeholder="DH"
                  required
                />
                <p style={{fontSize: '12px', color: '#666', margin: '4px 0 0 0'}}>
                  Ceci remplacera tous les symboles $ dans le système
                </p>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Enregistrement...' : 'Enregistrer les paramètres'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CompanySettings;
